/**
 * "Found in this service" on a service's Apps page: the Dockerfiles and Docker Compose files vfs-service found anywhere
 * in its folder (utils/runnablesModel.js), each with its path, Pin as a service (it becomes an item of the Apps list,
 * run as a session of its own) and Ignore (into a collapsed "Ignored (n)" list, with Unignore). The choices are the
 * project's (`.flow/project.json`), so every collaborator sees the same.
 *
 * With a vfs-service that does not list the service's builders (`howItRuns`), when a Dockerfile builds the service
 * itself, How it runs: Build from code | Use the Dockerfile (a picker when there are several), saved in the service's
 * `.flow/service.json` (`run: "docker"` + `dockerfile`; back to code removes them). Otherwise the Overview's How it
 * runs picks among every builder (BuilderPicker). Without Docker the Dockerfile is not offered: the plan's message and
 * Change plan, or, where this Flow has no Docker support (`dockerOff` 'cluster'), "Ask your Flow admin" (NoDockerNote).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select, message as toast } from 'antd';
import * as vfsClient from '../../utils/vfsClient';
import { parseSpec } from '../../utils/serviceStackModel';
import {
  dockerChoiceBlocked, dockerOffMessage, howItRunsChoice, ownDockerfiles, rememberedDockerOff, specWithChoice, splitFound,
} from '../../utils/runnablesModel';
import NoDockerNote from './NoDockerNote';
import { plural } from './ServiceTabs/common';

const KIND_LABELS = { compose: 'Compose', dockerfile: 'Dockerfile' };

/** "3 services: redis, envoy, openbao" for a compose file. */
const servicesText = (item) => {
  const names = (item.services || []).map((service) => service.name);
  return names.length ? `${plural(names.length, 'service')}: ${names.join(', ')}` : (item.error || 'No services');
};

/** Build from code | Use the Dockerfile, for the Dockerfiles that build the service itself. */
export function HowItRunsChoice({ projectId, serviceName, dockerfiles, docker = null, dockerOff = undefined, readOnly = false, onChanged = null }) {
  const specPath = `${serviceName}/.flow/service.json`;
  const [spec, setSpec] = useState(undefined);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    vfsClient.readFile(projectId, specPath).then((text) => parseSpec(text), () => null).then((value) => { if (live) { setSpec(value); } });
    return () => { live = false; };
  }, [projectId, specPath]);
  if (spec === undefined) { return null; }
  const { how, dockerfile } = howItRunsChoice(spec);
  const files = dockerfiles.map((item) => item.file);
  const picked = files.includes(dockerfile) ? dockerfile : files[0];
  const blocked = how === 'code' ? dockerChoiceBlocked(spec) : null;
  const noDocker = docker === false;
  const offReason = dockerOff === undefined ? rememberedDockerOff(projectId) : dockerOff;

  const save = async(nextHow, nextFile = picked) => {
    setSaving(true);
    try {
      const next = specWithChoice(spec, nextHow, nextFile);
      if (next) {
        await vfsClient.writeFile(projectId, specPath, `${JSON.stringify(next, null, 2)}\n`);
      } else {
        await vfsClient.deleteEntry(projectId, specPath);
      }
      setSpec(next);
      onChanged?.();
      toast.success(nextHow === 'docker' ? `${serviceName.replace(/^services\//, '')} runs with ${nextFile}` : `${serviceName.replace(/^services\//, '')} is built from its code`);
    } catch (error) {
      toast.error(error.message || 'How it runs could not be saved');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="apps-how" data-testid="apps-how-it-runs">
      <span className="apps-how-label">How it runs</span>
      <div className="apps-how-options" role="group" aria-label="How it runs">
        <Button aria-pressed={how === 'code'} data-testid="apps-how-code" disabled={readOnly || saving || how === 'code'} onClick={() => save('code')} type={how === 'code' ? 'primary' : 'default'}>
          Build from code
        </Button>
        <Button
          aria-pressed={how === 'docker'}
          data-testid="apps-how-docker"
          disabled={readOnly || saving || how === 'docker' || Boolean(blocked) || noDocker}
          onClick={() => save('docker')}
          title={blocked || (noDocker ? dockerOffMessage(offReason) : undefined)}
          type={how === 'docker' ? 'primary' : 'default'}
        >
          Use the Dockerfile
        </Button>
        {files.length > 1 && (
          <Select
            aria-label="Dockerfile"
            data-testid="apps-how-dockerfile"
            disabled={readOnly || saving}
            onChange={(file) => (how === 'docker' ? save('docker', file) : null)}
            options={files.map((file) => ({ value: file, label: file }))}
            size="middle"
            value={how === 'docker' ? dockerfile : picked}
          />
        )}
      </div>
      {blocked && <p className="apps-muted apps-small">{blocked}</p>}
      {noDocker && (
        <p className="apps-muted apps-small" data-testid="apps-how-no-docker"><NoDockerNote reason={offReason} /></p>
      )}
    </div>
  );
}

HowItRunsChoice.propTypes = {
  docker: PropTypes.bool,
  dockerOff: PropTypes.oneOf(['cluster', 'plan', null]),
  dockerfiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  serviceName: PropTypes.string.isRequired,
};

export default function FoundRunnables({
  projectId, serviceName, runnables, choices, onChoose, docker = null, readOnly = false, onOpen = null, onSpecChanged = null, howItRuns = true,
}) {
  const [busy, setBusy] = useState(null);
  const { shown, ignored } = splitFound(runnables, choices);
  const own = ownDockerfiles(runnables);
  if (!runnables?.length) { return null; }

  const choose = async(item, state) => {
    setBusy(item.path);
    try {
      await onChoose({ path: item.path, kind: item.kind, state });
    } catch (error) {
      toast.error(error.message || 'The choice could not be saved');
    } finally {
      setBusy(null);
    }
  };
  const row = (item, actions) => (
    <li className="apps-found-row" data-testid={`apps-found-${item.path}`} key={item.path}>
      <span className="apps-found-text">
        <span className="apps-found-name">
          <span className="apps-badge">{KIND_LABELS[item.kind]}</span>
          <code>{item.file}</code>
          {item.runsService
            ? <span className="apps-tag" data-testid={`apps-runs-service-${item.path}`}>Runs this service</span>
            : item.own && <span className="apps-tag">Builds this service</span>}
          {item.state === 'pinned' && <span className="apps-tag is-accent">Pinned</span>}
        </span>
        {item.kind === 'compose' && <span className="apps-muted apps-small">{servicesText(item)}</span>}
      </span>
      {!readOnly && <span className="apps-found-actions">{actions}</span>}
    </li>
  );

  return (
    <section className="apps-card apps-found" data-testid="apps-found">
      <h3>Found in this service</h3>
      <p className="apps-muted apps-small">Dockerfiles and Docker Compose files in this folder. Pin one to run it as an app of its own (a compose file lists its services), or ignore it.</p>
      {howItRuns && own.length > 0 && <HowItRunsChoice docker={docker} dockerfiles={own} onChanged={onSpecChanged} projectId={projectId} readOnly={readOnly} serviceName={serviceName} />}
      {shown.length > 0 && (
        <ul className="apps-found-list">
          {shown.map((item) => row(item, item.runsService ? (
            // The service itself runs through it (one entry, one session): nothing to pin; an older pin can go.
            item.state === 'pinned'
              ? <Button data-testid={`apps-unpin-${item.path}`} disabled={busy === item.path} onClick={() => choose(item, null)} size="small">Unpin</Button>
              : null
          ) : (
            <>
              {item.state === 'pinned' ? (
                <>
                  {onOpen && <Button onClick={() => onOpen(item)} size="small">Open</Button>}
                  <Button data-testid={`apps-unpin-${item.path}`} disabled={busy === item.path} onClick={() => choose(item, null)} size="small">Unpin</Button>
                </>
              ) : (
                <Button data-testid={`apps-pin-${item.path}`} disabled={busy === item.path} onClick={() => choose(item, 'pinned')} size="small">Pin as a service</Button>
              )}
              <Button data-testid={`apps-ignore-${item.path}`} disabled={busy === item.path} onClick={() => choose(item, 'ignored')} size="small" type="text">Ignore</Button>
            </>
          )))}
        </ul>
      )}
      {ignored.length > 0 && (
        <details className="apps-found-ignored" data-testid="apps-found-ignored">
          <summary>Ignored ({ignored.length})</summary>
          <ul className="apps-found-list">
            {ignored.map((item) => row(item, (
              <Button data-testid={`apps-unignore-${item.path}`} disabled={busy === item.path} onClick={() => choose(item, null)} size="small">Unignore</Button>
            )))}
          </ul>
        </details>
      )}
    </section>
  );
}

FoundRunnables.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.object).isRequired,
  docker: PropTypes.bool,
  // Show How it runs (Build from code | Use the Dockerfile) here: only when the Overview has no builder picker.
  howItRuns: PropTypes.bool,
  onChoose: PropTypes.func.isRequired,
  // (item) => shows the pinned file's own page in the Apps list.
  onOpen: PropTypes.func,
  // After How it runs changed the service's .flow/service.json.
  onSpecChanged: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  runnables: PropTypes.arrayOf(PropTypes.object),
  serviceName: PropTypes.string.isRequired,
};
